<script lang="ts" setup>
    import { reactive, watch, ref } from 'vue';

    import WindowSearchBase from '../../components/Search/WindowSearchBase.vue'

    import { WindowSearch } from '../../lib/ChoiceApi';
    import { WindowSearchRenderingInputs, WindowSearchRenderingListItem, WindowSearchColumnNameItem, WindowSearchAdditionalPlacement, WindowSearchDropdownAlignment } from '../../lib/Api/WindowSearch';

    const props = withDefaults(defineProps<{
        modelValue: WindowSearchRenderingListItem | null
        newLink?: boolean,
        newLinkTooltip?: string,
        disabled?: boolean,
        placeholder?: string,
        label?: string,
        additionalPlacement?: WindowSearchAdditionalPlacement,
        fixedSearchWidth?: number 
        dropdownWidth?: number,
        dropdownPlacement?: WindowSearchDropdownAlignment
    }>(), {
        newLink: false,
        newLinkTooltip: "Create a new Rendering Provider",
        disabled: false,
        additionalPlacement: WindowSearchAdditionalPlacement.Top,
        dropdownPlacement: WindowSearchDropdownAlignment.Left
    });

    const emit = defineEmits(['update:modelValue']);

    const columnNames = ref<WindowSearchColumnNameItem[]>([
        { columnName: "billingProviderId", tooltip: "Billing Provider ID", width: "70px" },
        { columnName: "providerName", tooltip: "Provider Name" },
        { columnName: "npi", tooltip: "National Provider ID", width: "120px" },
        { columnName: "active", tooltip: "Active Status", width: "30px" }
    ]);

    const formData = reactive<{
        searchResults: WindowSearchRenderingListItem[],
        searchValue: WindowSearchRenderingListItem | null,
        totalRows: number,
        maxReturnRows: number,
        disabled?: boolean
    }>({
        searchResults: [],
        searchValue: null,     
        totalRows: 0,
        maxReturnRows: 0,
        disabled: props.disabled || false
    });

    watch(() => props.modelValue, (_new, _old) => {
        if (_new !== _old) {
            formData.searchValue = _new;
        }   
    });

    watch(() => props.disabled, (_new, _old) => {
        if (_new !== _old) {
            formData.disabled = _new;
        }   
    });

    async function onSearch(searchText: string) { 
        const inputs: WindowSearchRenderingInputs = {
            searchText,
            onlyRendering: true
        };

        const respSearch = await WindowSearch.Rendering(inputs);
        const mappedResults = (respSearch?.searchList || []).map(item => ({
            ...item,
            active: item.active ? "Yes" : "No"
        }));
        formData.searchResults = mappedResults;
        formData.totalRows = respSearch?.totalRows || 0;
        formData.maxReturnRows = respSearch?.maxReturnRows || 0;
    }

    function onNew() {
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
        const left = (screenWidth - 800) / 2;
        const top = (screenHeight - 400) / 2;
        const window_features = `popup=yes,resizable=yes,width=1024,height=768,top=${top},left=${left}`;
        window.open("/officemd/screens/provider.asp?WT=Provider Setup&Qualifier=id&WindowID=0","Provider",window_features);
    }

    function onUpdateModelValue(value: WindowSearchRenderingListItem) {
        emit('update:modelValue', value);
    }

    function onReset() {
        formData.searchResults = [];
        formData.totalRows = 0;
        formData.maxReturnRows = 0;
    }
</script>

<template>
    <WindowSearchBase
        v-model="formData.searchValue" 
        :columns="columnNames"
        selectedColumn="providerName"
        :searchResults="formData.searchResults"
        :totalRows="formData.totalRows"
        :maxReturnRows="formData.maxReturnRows"
        :newLink="props.newLink"
        :disabled="formData.disabled"
        :placeholder="props.placeholder"
        :label="props.label"
        :additionalPlacement="props.additionalPlacement"
        :fixedSearchWidth="props.fixedSearchWidth"
        :dropdownWidth="props.dropdownWidth"
        :dropdownPlacement="props.dropdownPlacement"
        @onSearch="onSearch"
        @onNew="onNew"
        @update:modelValue="onUpdateModelValue"
        @onReset="onReset"
        />
</template>
